import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Io from 'react-icons/io'
import { batch, useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { toast } from 'react-toastify'

import { setCompany as setCompanyRedux } from '../../api/features/companySlice'
import { switchCompany } from '../../api/features/sessionActions'
import { setUser } from '../../api/features/userSlice'
import { useLazyUserFunctionQuery } from '../../api/services/usersApi'
import backgroundImage from '../../assets/images/Background.png'
import img from '../../assets/images/protocol-logo.png'
import { useCanSwitchCompany } from '../../hooks/useCanSwitchCompany'
import { getLandingRoute } from '../../utils/landingRoute'

const CompanySelect = () => {
  const [company, setCompany] = useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Request of the selection being applied; cleared by Return/unmount so a late response is ignored
  const pendingRequest = useRef(null)
  const [searchParams] = useSearchParams()
  const location = useLocation()

  const user = useSelector((state) => state.user.user)
  const currentCompany = useSelector((state) => state.company.company)

  // User already has functions only when coming from inside the app (company switch)
  const isSwitching = !!user?.functions?.length

  // On a switch keep the choice made at login; a user without the flag was restored from localStorage
  const remember = isSwitching ? user.remember !== false : !!searchParams.get('remember')

  const { companies: userCompanies, canSwitch } = useCanSwitchCompany()

  const [getUserFunctions] = useLazyUserFunctionQuery()

  const { t } = useTranslation()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Page of the current company, for leaving the picker without switching
  const currentLandingRoute = getLandingRoute(user?.functions) ?? '/homepage'

  useEffect(
    () => () => {
      pendingRequest.current = null
    },
    [],
  )

  const options = userCompanies?.map((company) => {
    const isCurrent = isSwitching && company.id === currentCompany?.id
    const label = company.name || 'company label'

    return {
      companyId: company.id,
      tax: company.tax,
      taxId: company.taxId,
      value: company.id,
      label: isCurrent ? `${label} (${t('current')})` : label,
      name: company.name,
      address: company.address,
      logo: company.logo,
      ip: company.ip,
      isDisabled: isCurrent,
    }
  })

  const releaseRequest = () => {
    pendingRequest.current = null
    setIsSubmitting(false)
  }

  const handleSelect = async (e) => {
    e.preventDefault()
    // A selection is already being applied
    if (pendingRequest.current) return
    if (!company) return toast.error(t('select_company'))

    const request = getUserFunctions({
      userId: user.id,
      companyId: company.companyId,
    })
    pendingRequest.current = request
    setIsSubmitting(true)

    try {
      const userFunctions = await request.unwrap()

      // Return was clicked or the page was left while waiting
      if (pendingRequest.current !== request) return

      const selectedCompany = userCompanies.find((comp) => comp.id === company.value)

      const functions = userFunctions[company.name]?.[0]
      const landingRoute = getLandingRoute(functions)

      // Checked before touching any state so a failed switch leaves the current session intact
      if (!landingRoute) {
        releaseRequest()
        return toast.error(t('no_functions_in_company'))
      }

      const userObj = { ...user, functions, remember }

      // Written before dispatching: api.js reads the token from localStorage for the refetches that follow
      if (remember) {
        localStorage.setItem('user', JSON.stringify(userObj))
        localStorage.setItem('company', JSON.stringify(selectedCompany))
      }

      // React 17 does not batch after await; batch so components never render a half-switched state
      batch(() => {
        if (isSwitching) {
          dispatch(switchCompany({ user: userObj, company: selectedCompany }))
        } else {
          dispatch(setUser(userObj))
          dispatch(setCompanyRedux(selectedCompany))
        }
        // Replace so browser Back doesn't reopen the picker; the page unmounts, nothing to release
        navigate(landingRoute, { replace: true })
      })
    } catch (error) {
      if (pendingRequest.current !== request) return
      releaseRequest()
      toast.error(error?.data?.message ?? t('err_occ'))
    }
  }

  const handleReturn = () => {
    pendingRequest.current = null
    if (!isSwitching) return navigate('/')
    // `from` is set by UserMenu, which pushed this page on top of it: go back instead of adding an entry.
    // companyId guards against a `from` of a company that is no longer active.
    const { from, companyId } = location.state ?? {}
    if (from && companyId === currentCompany?.id) return navigate(-1)
    navigate(currentLandingRoute, { replace: true })
  }

  const clientStyles = {
    control: (base, state) => ({
      ...base,
      height: '24px',
      background: '#ffffff',
      color: '#212529',
      width: '100%',
      borderRadius: 5,
      marginTop: 10,
      marginBottom: 10,
      boxShadow: '0px 3px 4px 0px #333',
    }),
    placeholder: (base) => {
      return {
        ...base,
        color: '#65aad5',
      }
    },
    dropdownIndicator: (base) => ({
      ...base,
      color: '#0d5c8d',
    }),
  }

  // Nothing to switch to (e.g. URL opened manually by a single-company user)
  if (isSwitching && userCompanies && !canSwitch) {
    return <Navigate to={currentLandingRoute} replace />
  }

  return (
    <>
      <div
        className="w-full h-screen flex items-center justify-center "
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="relative flex flex-col w-[310px] lg:w-[397px] h-[487px] border-[1px] border-[#D5D9EC] rounded-t-[20px] rounded-b-[20px]">
          <div className="flex flex-col w-[310px] lg:w-[397px] h-[361px] gap-[40px] pt-[60px] pr-[40px] pb-[40px] pl-[40px] bg-[#006EAB] rounded-t-[20px]">
            <div className="flex lg:w-[266px] h-[80px] p-[8px] gap-[12px] justify-center items-center">
              <img src={img} className="w-[64px] h-[64px]" alt="logo" />
              <p className="lg:w-[174px] h-[45px] font-bold text-4xl text-[#FFFFFF] text-center leading-[45.25px] ">
                PROTOCOL
              </p>
            </div>

            <form className="w-[250px] lg:w-[317px] h-[141px] flex flex-col gap-[12px]">
              <div className="mb-10 w-full flex flex-col justify-center items-center mt-6">
                <Select
                  options={options}
                  styles={clientStyles}
                  placeholder={t('company')}
                  className="w-full lg:w-4/5"
                  isDisabled={isSubmitting}
                  onChange={(e) => setCompany(e)}
                />
              </div>
            </form>
          </div>
          <div className="flex  w-[310px] lg:w-[397px] h-[126px] py-5 px-10 bg-[#E7EAF8] rounded-b-[20px] items-center justify-center gap-[12px]">
            <button
              className="flex justify-center items-center w-[317px] h-[48px] rounded-[4px] py-[12px] px-[20px]  gap-[20px] bg-white text-[#006EAB] font-semibold font-popins text-base leading-6 "
              onClick={handleReturn}
            >
              <div className="flex justify-evenly w-full items-center">
                <Io.IoIosArrowBack className="w-6 h-6" />
                <p className="mb-0">{t('return')}</p>
              </div>
            </button>
            <button
              className="flex justify-center items-center w-[317px] h-[48px] rounded-[4px] py-[12px] px-[20px]  gap-[20px] bg-[#006EAB] font-semibold font-popins text-base leading-6 text-[#FCFCFC] disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSubmitting}
              onClick={handleSelect}
            >
              {t('submit_2')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default CompanySelect
